/**
 * PipelineToolbar — reusable close-window + sort + saved-views bar.
 *
 * Used across Leads, Quotations, Approvals, Proforma Invoices, Customers,
 * and My Team. Each host page controls the actual filter state; this
 * component is a pure presenter that emits changes upward.
 *
 * Props:
 *   window            current preset key ('all', 'this_month', …)
 *   onWindowChange    (key) => void
 *   sortBy            current sort key ('created_desc', …)
 *   onSortChange      (key) => void
 *   forecastOverdueOnly  bool                (Leads/Quotations only)
 *   onForecastOverdueChange (bool) => void   (optional — omit for tabs w/o leads)
 *   pageKey           string — namespace for saved views (e.g. 'leads')
 *   showForecastToggle  bool — whether to render the pill (default true)
 *   sortOptions       optional custom sort options (defaults to SORT_OPTIONS)
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Funnel, WarningOctagon, BookmarkSimple, X, Check } from '@phosphor-icons/react';
import { CLOSE_WINDOW_PRESETS, SORT_OPTIONS } from '../lib/pipelineFilters';

import { notify } from '../lib/notify';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PipelineToolbar({
  window: activeWindow,
  onWindowChange,
  sortBy,
  onSortChange,
  forecastOverdueOnly = false,
  onForecastOverdueChange,
  pageKey,
  showForecastToggle = true,
  showWindowChips = true,
  sortOptions = SORT_OPTIONS,
  extraRight = null,
}) {
  const [views, setViews] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pageKey) return;
    axios.get(`${API}/saved-views?page_key=${pageKey}`).then(
      r => setViews(r.data.views || []),
    ).catch(() => setViews([]));
  }, [pageKey]);

  const applyView = (view) => {
    if (!view) return;
    const s = view.settings || {};
    if (s.window !== undefined) onWindowChange?.(s.window);
    if (s.sort !== undefined)   onSortChange?.(s.sort);
    if (s.forecast_overdue_only !== undefined && onForecastOverdueChange) {
      onForecastOverdueChange(!!s.forecast_overdue_only);
    }
  };

  const saveView = async () => {
    if (!newViewName.trim()) return;
    setSaving(true);
    try {
      const payload = {
        page_key: pageKey,
        name: newViewName.trim(),
        settings: {
          window: activeWindow,
          sort: sortBy,
          forecast_overdue_only: !!forecastOverdueOnly,
        },
      };
      const r = await axios.post(`${API}/saved-views`, payload);
      setViews(prev => [...prev, r.data.view]);
      setShowSaveModal(false);
      setNewViewName('');
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to save view');
    } finally { setSaving(false); }
  };

  const deleteView = async (viewId) => {
    if (!window?.confirm?.('Delete this saved view?')) return;
    try {
      await axios.delete(`${API}/saved-views/${viewId}`);
      setViews(prev => prev.filter(v => v.id !== viewId));
    } catch { /* silent */ }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 py-2" data-testid={`pipeline-toolbar-${pageKey}`}>
      {/* Close-window preset chips */}
      {showWindowChips && (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`window-chips-${pageKey}`}>
        <Funnel size={14} className="text-[#6D8E87]" weight="duotone" />
        {CLOSE_WINDOW_PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => onWindowChange?.(p.key)}
            data-testid={`${pageKey}-${p.testId}`}
            className={`px-2.5 py-1 text-[11px] rounded-full border transition ${
              activeWindow === p.key
                ? 'bg-[#00a69c] text-white border-[#00a69c] shadow-sm'
                : 'bg-white text-[#3B5A54] border-[#D5E8E4] hover:border-[#00a69c]'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      )}

      {/* Forecast-overdue toggle (Leads/Quotations only) */}
      {showForecastToggle && onForecastOverdueChange && (
        <button
          onClick={() => onForecastOverdueChange(!forecastOverdueOnly)}
          data-testid={`forecast-overdue-toggle-${pageKey}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-full border transition ${
            forecastOverdueOnly
              ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
              : 'bg-white text-rose-700 border-rose-200 hover:border-rose-500'
          }`}
        >
          <WarningOctagon size={12} weight="fill" />
          Overdue forecast only
        </button>
      )}

      {/* Right cluster: sort + saved views */}
      <div className="ml-auto flex items-center gap-2">
        {extraRight}

        <select
          value={sortBy}
          onChange={e => onSortChange?.(e.target.value)}
          className="border border-[#D5E8E4] rounded px-2 py-1 text-xs bg-white text-[#3B5A54] focus:outline-none focus:border-[#00a69c]"
          data-testid={`sort-select-${pageKey}`}
        >
          {sortOptions.map(o => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>

        {pageKey && (
          <SavedViewMenu
            views={views}
            onApply={applyView}
            onDelete={deleteView}
            onOpenSave={() => setShowSaveModal(true)}
            pageKey={pageKey}
          />
        )}
      </div>

      {/* Save-view mini modal */}
      {showSaveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-80 p-4"
            onClick={e => e.stopPropagation()}
            data-testid={`save-view-modal-${pageKey}`}
          >
            <div className="text-sm font-semibold text-[#00594F] mb-2">Save current view</div>
            <div className="text-[11px] text-[#6D8E87] mb-3">
              Window: <b>{activeWindow || 'all'}</b> · Sort: <b>{sortBy}</b>
              {forecastOverdueOnly ? ' · Overdue-only' : ''}
            </div>
            <input
              autoFocus
              value={newViewName}
              onChange={e => setNewViewName(e.target.value)}
              placeholder="e.g. My Q1 Board"
              className="w-full border border-[#D5E8E4] rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#00a69c]"
              data-testid="save-view-name-input"
              onKeyDown={e => e.key === 'Enter' && saveView()}
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-3 py-1 text-xs text-[#6D8E87] hover:bg-slate-50 rounded"
              >Cancel</button>
              <button
                onClick={saveView}
                disabled={!newViewName.trim() || saving}
                className="px-3 py-1 text-xs bg-[#00a69c] text-white rounded hover:bg-[#008a82] disabled:opacity-50"
                data-testid="save-view-confirm-btn"
              >{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SavedViewMenu({ views, onApply, onDelete, onOpenSave, pageKey }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#D5E8E4] rounded bg-white text-[#3B5A54] hover:border-[#00a69c]"
        data-testid={`saved-views-btn-${pageKey}`}
      >
        <BookmarkSimple size={12} weight="duotone" />
        Views {views.length > 0 && <span className="text-[10px] text-[#6D8E87]">({views.length})</span>}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-[#D5E8E4] rounded-lg shadow-lg z-40 max-h-72 overflow-auto" data-testid={`saved-views-menu-${pageKey}`}>
          {views.length === 0 && (
            <div className="px-3 py-3 text-[11px] text-[#6D8E87]">No saved views yet.</div>
          )}
          {views.map(v => (
            <div
              key={v.id}
              className="group flex items-center justify-between px-3 py-2 text-xs hover:bg-[#F4F9F7] cursor-pointer"
              onMouseDown={() => onApply(v)}
              data-testid={`saved-view-row-${v.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Check size={12} className="text-[#00a69c] opacity-70" />
                <div className="min-w-0">
                  <div className="text-[#3B5A54] truncate">{v.name}</div>
                  <div className="text-[10px] text-[#6D8E87] truncate">
                    {v.settings?.window || 'all'} · {v.settings?.sort || 'created_desc'}
                  </div>
                </div>
              </div>
              <button
                onMouseDown={e => { e.stopPropagation(); onDelete(v.id); }}
                className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 shrink-0 ml-2"
                data-testid={`saved-view-delete-${v.id}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <div className="border-t border-[#D5E8E4] mt-1">
            <button
              onMouseDown={onOpenSave}
              className="w-full text-left px-3 py-2 text-xs text-[#00a69c] hover:bg-[#F4F9F7] font-medium"
              data-testid={`save-current-view-btn-${pageKey}`}
            >
              + Save current view
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
